import React, { useState } from 'react';
import styles from './CourseCard.module.scss';
import Button from '../Button';
import classNames from 'classnames';
import { Checkbox, Icon } from 'Components';
import ToggleButton from '../ToggleButton';
import DropdownMenu from '../Dropdown';
import AssignmentBadge from 'LearningManager/components/AssignmentBadge';

interface Props {
	course: {
		id: number;
		title: string;
		meta?: string;
		tag?: string;
		tags?: string[];
		image: string;
		courseCount?: string;
		isMandatory?: boolean;
		isSelected?: boolean;
	};
	index: number;
	htmlId?: string;
	onClick: () => void;
	onSelect?: (id: number) => void;
	showCheckbox?: boolean;
	checked?: boolean;
	onCheck?: (id: number, checked: boolean) => void;
	showMenu?: boolean;
	onMenuAction?: (id: number, action: string) => void;
	ctaLabel?: string;
	ctaVariant?: 'border' | 'borderless';
	ctaClassName?: string;
	checkedClassName?: string;
	hideBadge?: boolean;
	hideRequiredSection?: boolean;
	hideMandatoryCover?: boolean;
	isRequired?: boolean;
	onRequiredChange?: (id: number, isRequired: boolean) => void;
}

interface RequiredSectionProps {
	checked: boolean;
	onChange: (checked: boolean) => void;
}

const RequiredSection: React.FC<RequiredSectionProps> = ({
	checked,
	onChange
}) => {
	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	return (
		<div className={styles['required-section']} onClick={handleToggle}>
			<div className={styles.heading}>Required</div>
			<ToggleButton checked={checked} size="small" onChange={onChange} />
		</div>
	);
};

const CourseCard: React.FC<Props> = ({
	course,
	index,
	htmlId,
	onClick,
	onSelect,
	showCheckbox,
	checked,
	onCheck,
	showMenu,
	onMenuAction,
	ctaLabel = 'Details',
	ctaVariant,
	ctaClassName,
	checkedClassName,
	hideBadge = false,
	hideRequiredSection = false,
	hideMandatoryCover = false,
	isRequired,
	onRequiredChange
}) => {
	const [localRequired, setLocalRequired] = useState(true);

	const handleRequiredChange = (value: boolean) =>
		onRequiredChange
			? onRequiredChange(course.id, value)
			: setLocalRequired(value);

	const handleDetailsClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onClick();
	};

	return (
		<div
			id={htmlId}
			className={classNames(styles['course-card'], {
				[styles['course-card--selected']]: course.isSelected
			})}
			onClick={
				onSelect && !course.isMandatory
					? () => onSelect(course.id)
					: () => null
			}
		>
			{course.isSelected && (
				<div className={classNames(styles.checked, checkedClassName)}>
					<Icon type="check" />
				</div>
			)}
			{course.isMandatory && !hideMandatoryCover && (
				<div className={styles['card-cover']}>
					<span className={styles['message']}>
						This course is part of your selected learning path
					</span>
				</div>
			)}
			<div className={styles['image-wrapper']}>
				<img src={course.image} alt={course.title} />
				{showCheckbox && (
					<div
						className={styles['card-checkbox']}
						onClick={(e) => e.stopPropagation()}
					>
						<Checkbox
							checked={checked}
							onChange={(e) =>
								onCheck?.(course.id, e.target.checked)
							}
						/>
					</div>
				)}
				{showMenu && (
					<div
						className={styles['card-menu']}
						onClick={(e) => e.stopPropagation()}
					>
						<DropdownMenu
							arrow={false}
							onChange={(key: string) =>
								onMenuAction?.(course.id, key)
							}
							options={[
								{ key: 'view-details', label: 'View details' },
								{
									key: 'reset-completion',
									label: 'Reset Completion'
								},
								{
									key: 'unassign',
									label: 'Unassign',
									danger: true
								}
							]}
						>
							<div className={styles['more-btn']}>
								<Icon type="optionIcon" role="img" />
							</div>
						</DropdownMenu>
					</div>
				)}
				{course.courseCount ? (
					<div className={styles.courseCount}>
						{course.courseCount}
					</div>
				) : (
					!hideBadge &&
					!showCheckbox &&
					!showMenu && <div className={styles.badge}>{index}</div>
				)}
			</div>

			<div className={styles.content}>
				<div className={styles.title}>{course.title}</div>
				{course.meta && (
					<div className={styles.meta}>{course.meta}</div>
				)}
				{course.tags && course.tags.length > 0 ? (
					<div className={styles.tags}>
						{course.tags.map((t) => (
							<AssignmentBadge key={t} type={t} />
						))}
					</div>
				) : (
					course.tag && (
						<span className={styles.tag}>{course.tag}</span>
					)
				)}
			</div>

			<Button
				fullWidth
				size="small"
				variant={ctaVariant}
				className={ctaClassName}
				onClick={handleDetailsClick}
			>
				{ctaLabel}
			</Button>

			{course.isSelected && !hideRequiredSection && (
				<RequiredSection
					checked={isRequired ?? localRequired}
					onChange={handleRequiredChange}
				/>
			)}
		</div>
	);
};

export default CourseCard;
